import { Injectable } from "@angular/core";
import { SwUpdate } from "@angular/service-worker";
import * as Sentry from "@sentry/angular";
import { filter } from "rxjs/operators";

export const SW_RECOVERY_KEY = "flip.swRecoveryAt";
/** One recovery per window, so a worker that is stuck for a reason a reload cannot fix never loops. */
export const SW_RECOVERY_WINDOW_MS = 5 * 60_000;

/**
 * FLIP-693. Gets an installed app out of a stuck service-worker state.
 *
 * Angular's service worker keeps a saved copy of the page. If that copy can no longer be served (a file it
 * needs is gone after a release) the worker reports `unrecoverable` and, until something acts on it, every
 * launch shows the same broken page: after LGL 1.5.2 the Android app showed a blank screen, and clearing the
 * app's cache did not help because the saved copy lives in service-worker storage.
 *
 * Recovery removes the worker and its caches and reloads, so the next load comes straight from the server.
 * It runs at most once per five minutes, so a problem a reload cannot fix does not become a reload loop.
 *
 * The page's own boot guard in index.html covers the case where the main script never starts; this service
 * covers a stuck worker once the app is running.
 */
@Injectable({ providedIn: "root" })
export class ServiceWorkerRecoveryService {
  constructor(private swUpdate: SwUpdate) {}

  start(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.unrecoverable.subscribe((event) => {
      void this.recover(`unrecoverable: ${event.reason}`);
    });

    this.swUpdate.versionUpdates
      .pipe(filter((event) => event.type === "VERSION_INSTALLATION_FAILED"))
      .subscribe((event: any) => {
        // The update could not be verified. The worker keeps the version it has, so this is only recorded.
        Sentry.captureMessage(`Service worker could not install a version: ${event.error ?? "unknown"}`, "warning");
      });
  }

  /** Returns true when it started a recovery, false when one already ran within the window. */
  async recover(reason: string): Promise<boolean> {
    if (!this.mayRecover()) {
      return false;
    }
    this.markRecovered();
    Sentry.captureMessage(`Service worker recovery: ${reason}`, "warning");

    try {
      const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      // Carry on: clearing the caches and reloading may still be enough.
    }
    try {
      const names = (await caches?.keys?.()) ?? [];
      await Promise.all(names.map((name) => caches.delete(name)));
    } catch {
      // Same: reload anyway.
    }

    this.reload();
    return true;
  }

  protected reload(): void {
    window.location.reload();
  }

  private mayRecover(): boolean {
    const last = Number(sessionStorage.getItem(SW_RECOVERY_KEY) || 0);
    return Date.now() - last >= SW_RECOVERY_WINDOW_MS;
  }

  private markRecovered(): void {
    sessionStorage.setItem(SW_RECOVERY_KEY, String(Date.now()));
  }
}
